// report 模組共用工具（批次 14，Gitea issue #22）。
//
// 行號基準＝ePortfolio_work/ePortfolio-tsgh（worktree 內 src/ePortfolio 副本可能差 1 行）。
//
// - isAfterOrSame：js-my/common.js:650-655 的鏡像（moment 語意——任一端非空才比較、
//   無效日期字串 → false → 呼叫端 alert『日期選取不正確!』；day 粒度）。
// - 月處理：planFormExport.jsp doExport（:122-133）／studentDeptSatisfy.jsp（:66-73）——
//   長度 7（YYYY-MM）→ 起日補 -01、訖日補當月最後一天（moment endOf('month') 的鏡像，
//   以 Date.UTC 計算，禁 Math.random／Date.now）。
// - ReportRow：查詢表單一列（label＋紅星＋控制項＋欄位錯誤）。
// - runReportExport：匯出頁共用動作——驗證鏉由各頁 chain 負責；這裡只做
//   `dataSource.list(pageId, { filters, export: true })`（D-1 第 2a 點：匯出＝list #export）
//   → 成功 info(t('export.notice'))（雛形不落地檔案，B1-3 模式）／失敗 alert
//   （legacy failCallback：responseHtml=='NoData'→『查不到資料!』、其他『下載檔案失敗！』；
//   雛形 mock 無法區分 NoData → 以頁面 legacy failure 訊息優先、退回『下載檔案失敗！』，
//   差異標註【雛形值待實機校準】）。
// - ReportPanel：匯出頁 body 共用外殼（LegacyContractModals＋ContentPanel＋動作鈕列＋說明 ol）。
// 【證據等級】§7 第 3 級（原始碼）。

import type { ReactElement, ReactNode } from 'react';
import { panelHeadingLevelFor } from '@/app/panel-heading-levels';
import { ContentPanel, useDialogs } from '@/components/layouts';
import { LegacyContractModals } from '@/components/layouts/LegacyContractModals';
import { t } from '@/messages/zh-TW';
import { formSpecFor } from '@/mocks/legacy-form-specs';
import { resolveLegacyMessageForAction, type DataSource } from '@/mocks';
import { toUiKey } from '@/pages/templates/shared';

/** common.js:650-655 isAfterOrSame 的鏡像（day 粒度；無效日期→false）。 */
export function isAfterOrSame(before: string, after: string): boolean {
  if (before === '' || after === '') {
    return true; // legacy：任一端空 → true（由呼叫端的 required/其他檢核把關）
  }
  const beforeDate = new Date(before);
  const afterDate = new Date(after);
  if (Number.isNaN(beforeDate.getTime()) || Number.isNaN(afterDate.getTime())) {
    return false; // moment(invalid).isAfter/isSame 皆 false → 呼叫端 alert
  }
  const beforeDay = Date.UTC(beforeDate.getFullYear(), beforeDate.getMonth(), beforeDate.getDate());
  const afterDay = Date.UTC(afterDate.getFullYear(), afterDate.getMonth(), afterDate.getDate());
  return afterDay >= beforeDay;
}

/** 訓練期間月值（YYYY-MM）→ 起日 YYYY-MM-01（planFormExport.jsp:124-127 鏡像）。 */
export function monthStartOf(value: string): string {
  return `${value}-01`;
}

/** 訓練期間月值（YYYY-MM）→ 訖日當月最後一天（moment endOf('month') 鏡像，:128-130）。 */
export function monthEndOf(value: string): string {
  const parts = value.split('-').map((part) => Number.parseInt(part, 10));
  const year = parts[0] ?? Number.NaN;
  const month = parts[1] ?? Number.NaN;
  if (Number.isNaN(year) || Number.isNaN(month)) {
    return value;
  }
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return `${value}-${String(lastDay).padStart(2, '0')}`;
}

/** 匯出 payload 的月份欄位後處理（planFormExport:122-133／studentDeptSatisfy:66-73 鏡像）。 */
export function monthRangePayload(
  values: Readonly<Record<string, string>>,
  startKey: string,
  endKey: string,
): { start: string; end: string } {
  let start = values[startKey] ?? '';
  let end = values[endKey] ?? '';
  if (start !== '' && start.length === 7) {
    start = monthStartOf(start);
  }
  if (end !== '' && end.length === 7) {
    end = monthEndOf(end);
  }
  return { start, end };
}

/** 查詢表單一列（label＋紅星＋控制項＋欄位錯誤）。 */
export function ReportRow(props: {
  pageId: string;
  name: string;
  label: string;
  star?: boolean | undefined;
  error?: string | undefined;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="flex items-start gap-2">
      {/* 批次 50（工作項 4）：htmlFor 字尾 toUiKey kebab 化，與 children 的 kebab 字面 uiId 成對
          （name 參數本身是 name/payload key 域，值不動；已 kebab 值（log-date-start）經 toUiKey 不變）；
          error div（:95）是 error/payload key 域＝opaque 另案不動。 */}
      <label className="w-24 shrink-0 text-sm" htmlFor={`${props.pageId}-field-${toUiKey(props.name)}`}>
        {props.star === true ? <span className="text-danger">*</span> : null}
        {props.label}
      </label>
      <div>
        {props.children}
        {props.error === undefined ? null : (
          <div data-ui-id={`${props.pageId}-error-${props.name}`} className="text-xs text-danger">
            {props.error}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * 匯出頁共用動作（各頁 doExport/doQuery 的 fileDownload 段鏡像）。
 * **必須由呼叫端傳入 hooks 取得的 dataSource/dialogs**（本函式在事件 handler 內執行，
 * 不能自己呼叫 hooks）。
 * list(export:true) → 成功 info(export.notice)／失敗 alert（查不到資料!/下載檔案失敗！）。
 * filters 由各頁 payload 組裝（照 legacy params：named 欄位＋id 讀值欄位全部帶出）。
 */
export async function runReportExport(props: {
  pageId: string;
  filters: Record<string, string>;
  dataSource: DataSource;
  dialogs: ReturnType<typeof useDialogs>;
}): Promise<void> {
  try {
    await props.dialogs.processing.wrap(
      props.dataSource.list(props.pageId, { page: 1, pageSize: 50, filters: props.filters, export: true }),
    );
    await props.dialogs.info(t('export.notice')); // 雛形不產生檔案（B1-3 模式）
  } catch (cause: unknown) {
    // failCallback（各頁同款）：responseHtml=='NoData'→查不到資料!／其他→下載檔案失敗！。
    // 雛形 mock 無法區分 NoData → legacy failure 訊息優先，退回『下載檔案失敗！』。
    void cause;
    await props.dialogs.alert(
      resolveLegacyMessageForAction(props.pageId, 'failure', 'export')?.text ?? '下載檔案失敗！',
    );
  }
}

/** 匯出頁 body 的共用外殼：LegacyContractModals＋ContentPanel＋查詢區＋動作鈕列＋說明 ol。 */
export function ReportPanel(props: {
  pageId: string;
  title: string;
  actions?: ReactNode | undefined;
  note?: readonly string[] | undefined;
  children: ReactNode;
}): ReactElement {
  // 批次 36（GNL-005-b，issue #38）：查詢/參數區＋動作鈕包 <form> 鏡像 legacy
  // （裁決「補齊 form（分段）」；attr 由 LEGACY_FORM_SPECS 鏡像——ATTR_ALLOW 含
  // action/method/target、不含 class/id/onsubmit → 寫死同值即 diff 等值）。
  // 白名單制：未登錄（R-diff verdict SKIP 群，包了會位移劣化）＝不包，維持原 div 平鋪。
  // mock-only 不真送出：onSubmit preventDefault（送出語意由各 body 的 onClick 行為承擔）。
  const formSpec = formSpecFor(props.pageId);
  const queryArea = (
    <>
      <div data-ui-id={`${props.pageId}-query`} className="mb-4 grid grid-cols-2 gap-x-4 gap-y-2">
        {props.children}
      </div>
      {props.actions === undefined ? null : (
        <div data-ui-id={`${props.pageId}-actions`} className="mb-3 flex gap-2">
          {props.actions}
        </div>
      )}
    </>
  );
  return (
    <div data-ui-id={props.pageId}>
      {/* D-3：spec modal 照舊渲染（批次 11「spec modal 與自訂 body 並存」先例）。 */}
      <LegacyContractModals pageId={props.pageId} />
      <ContentPanel
        uiId={`${props.pageId}-panel`}
        title={props.title}
        headingLevel={panelHeadingLevelFor(props.pageId)}
      >
        {formSpec === undefined ? (
          queryArea
        ) : (
          <form
            data-ui-id={`${props.pageId}-query-form`}
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
            }}
            {...(formSpec.action === undefined ? {} : { action: formSpec.action })}
            {...(formSpec.method === undefined ? {} : { method: formSpec.method })}
            {...(formSpec.target === undefined ? {} : { target: formSpec.target })}
          >
            {queryArea}
          </form>
        )}
        {props.note === undefined ? null : (
          <div data-ui-id={`${props.pageId}-note`} className="text-sm text-ink-muted">
            說明:
            <ol className="ml-4 list-decimal">
              {props.note.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ol>
          </div>
        )}
      </ContentPanel>
    </div>
  );
}
